
import React, { JSX, useCallback, useEffect, useState } from 'react'
import {
  ArrowPathIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  TrashIcon
} from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import { DeletionResult, MerchApp } from 'types/api'
import listMerchAppsQuery from 'graphql/listMerchApps.graphql'
import putMerchAppMutation from 'graphql/putMerchApp.graphql'
import deleteMerchAppMutation from 'graphql/deleteMerchApp.graphql'

const APP_KEY_RE = /^[a-z0-9-]{1,64}$/

const DISTRIBUTIONS: Array<{ value: string, label: string }> = [
  { value: 'custom', label: 'custom: one app per client (live)' },
  { value: 'public', label: 'public: App Store listing' }
]

const DAY_MS = 24 * 60 * 60 * 1000

interface AppFormState {
  appKey: string
  label: string
  distribution: string
  ownerEmail: string
  installLinkUrl: string
}

const emptyAppForm = (): AppFormState => ({
  appKey: '',
  label: '',
  distribution: 'custom',
  ownerEmail: '',
  installLinkUrl: ''
})

const saveResultCopy = ({ email, appKey }: { email: string, appKey: string }): string =>
  `${email} now owns ${appKey}.`

interface ExpiryState {
  text: string
  tone: 'expired' | 'urgent' | 'normal'
}

const expiryState = (expiresAt: string | null, nowMs: number): ExpiryState | null => {
  if (expiresAt === null || expiresAt === '') return null
  const ms = new Date(expiresAt).getTime()
  if (Number.isNaN(ms)) return null
  const remaining = ms - nowMs
  if (remaining <= 0) {
    return { text: 'EXPIRED regenerate in the Partner Dashboard', tone: 'expired' }
  }
  if (remaining < DAY_MS) return { text: 'expires today', tone: 'urgent' }
  if (remaining < 2 * DAY_MS) return { text: 'expires tomorrow', tone: 'urgent' }
  const days = Math.floor(remaining / DAY_MS)
  return { text: `expires in ${days} days`, tone: days <= 2 ? 'urgent' : 'normal' }
}

const EXPIRY_TONE_CLASS: Record<ExpiryState['tone'], string> = {
  expired: 'font-semibold text-red-300',
  urgent: 'font-medium text-amber-300',
  normal: 'text-dm-text-secondary'
}

const ExpiryLine = ({ state }: { state: ExpiryState | null }): JSX.Element => {
  if (state === null) {
    return <span className='text-[10px] text-dm-text-accent'>no install link on file</span>
  }
  return (
    <span
      data-testid='merch-app-link-expiry'
      className={`text-[10px] ${EXPIRY_TONE_CLASS[state.tone]}`}
    >
      {state.text}
    </span>
  )
}

const CopyButton = ({ value, label }: { value: string, label: string }): JSX.Element => {
  const [copied, setCopied] = useState(false)
  const handleCopy = async (): Promise<void> => {
    try {
      await globalThis.navigator.clipboard.writeText(value)
      setCopied(true)
      globalThis.setTimeout(() => setCopied(false), 1500)
    } catch {
    }
  }
  return (
    <button
      type='button'
      onClick={() => { void handleCopy() }}
      title={`Copy ${label}`}
      className='inline-flex shrink-0 items-center text-dm-text-accent hover:text-dm-text-secondary'
    >
      {copied
        ? <ClipboardDocumentCheckIcon className='size-4 text-emerald-400' aria-hidden='true' />
        : <ClipboardDocumentIcon className='size-4' aria-hidden='true' />}
      <span className='sr-only'>Copy {label}</span>
    </button>
  )
}

const DistributionCell = ({ distribution }: { distribution: string | null }): JSX.Element => {
  if (distribution === null || distribution === '') {
    return (
      <span
        data-testid='merch-app-distribution-unset'
        title='No ssm parameter installs under this appKey cannot start until it exists'
        className='inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-400 ring-1 ring-inset ring-amber-500/30'
      >
        <ExclamationTriangleIcon className='size-3' aria-hidden='true' />
        SSM not set
      </span>
    )
  }
  if (distribution === 'public') {
    return (
      <span
        title='Public app installs are refused'
        className='inline-flex items-center rounded-full bg-dm-bg-accent/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent ring-1 ring-inset ring-white/10'
      >
        public
      </span>
    )
  }
  return (
    <span
      title='Custom distribution one app per client, installed from a Partner Dashboard link'
      className='inline-flex items-center rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-400 ring-1 ring-inset ring-emerald-500/30'
    >
      custom
    </span>
  )
}

const InstallLinkCell = ({ app, nowMs }: { app: MerchApp, nowMs: number }): JSX.Element => {
  const state = expiryState(app.installLinkExpiresAt, nowMs)
  const url = app.installLinkUrl
  if (url === null || url === '') {
    return (
      <div className='flex flex-col gap-0.5'>
        <span className='text-dm-text-accent'>—</span>
        <ExpiryLine state={state} />
      </div>
    )
  }
  return (
    <div className='flex flex-col gap-0.5'>
      <div className='flex items-center gap-1.5'>
        <span className='max-w-[16rem] truncate font-mono text-dm-text-primary' title={url}>{url}</span>
        <CopyButton value={url} label='install link' />
      </div>
      <ExpiryLine state={state} />
      {app.installLinkIssuedAt !== null && (
        <span className='text-[10px] text-dm-text-accent'>
          issued {formatLocalDateTime(app.installLinkIssuedAt)}
        </span>
      )}
    </div>
  )
}

const AppsBody = ({
  apps,
  loading,
  error,
  nowMs,
  deletingAppKey,
  onDelete,
  onRetry
}: {
  apps: MerchApp[] | null
  loading: boolean
  error: string | null
  nowMs: number
  deletingAppKey: string | null
  onDelete: (app: MerchApp) => void
  onRetry: () => void
}): JSX.Element => {
  if (error !== null) {
    return (
      <div className='mt-3 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'>
        <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
        <span className='flex-1'>{error}</span>
        <button
          onClick={onRetry}
          className='shrink-0 rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium hover:bg-red-500/20'
        >
          Retry
        </button>
      </div>
    )
  }
  if (loading || apps === null) {
    return (
      <div className='flex items-center justify-center py-6'>
        <LoadingSpinner />
      </div>
    )
  }
  if (apps.length === 0) {
    return (
      <p className='mt-3 rounded-lg border border-dashed border-white/10 px-4 py-6 text-center text-xs text-dm-text-secondary'>
        No Shopify apps registered yet add one below, after its
        client_id / client_secret / distribution parameters exist in SSM.
      </p>
    )
  }
  return (
    <div className='mt-3 overflow-x-auto'>
      <table className='w-full min-w-[980px] text-left text-[11px]'>
        <thead>
          <tr className='border-b border-white/10 text-[10px] font-semibold uppercase tracking-wider text-dm-text-accent'>
            <th className='py-1.5 pr-3'>App key</th>
            <th className='py-1.5 pr-3'>Label</th>
            <th className='py-1.5 pr-3' title='Resolved from SSM at read time the single source of truth'>Distribution</th>
            <th className='py-1.5 pr-3' title='Every shop installed under this app key binds to this account. Resolved from the user directory at read time never stored'>Owner</th>
            <th className='py-1.5 pr-3'>Shops</th>
            <th className='py-1.5 pr-3' title='Generated by hand in the Dev/Partner Dashboard expires 7 days after issue'>Install link</th>
            <th className='py-1.5' />
          </tr>
        </thead>
        <tbody>
          {apps.map((app) => (
            <tr key={app.appKey} className='border-b border-white/5 align-top text-dm-text-secondary'>
              <td className='py-1.5 pr-3 font-mono text-dm-text-primary'>{app.appKey}</td>
              <td className='max-w-[10rem] truncate py-1.5 pr-3'>{app.label ?? '—'}</td>
              <td className='py-1.5 pr-3'><DistributionCell distribution={app.distribution} /></td>
              <td
                className={`max-w-[14rem] truncate py-1.5 pr-3 ${app.ownerEmail === null ? 'italic text-dm-text-accent' : ''}`}
                title={app.ownerAccountId ?? undefined}
              >
                {app.ownerEmail ?? 'unknown user'}
              </td>
              <td className='py-1.5 pr-3 tabular-nums'>{app.shopCount}</td>
              <td className='py-1.5 pr-3'><InstallLinkCell app={app} nowMs={nowMs} /></td>
              <td className='py-1.5'>
                <button
                  type='button'
                  onClick={() => onDelete(app)}
                  disabled={deletingAppKey !== null || app.shopCount > 0}
                  title={app.shopCount > 0
                    ? 'Shops are still registered under this app key remove them first'
                    : 'Delete this registry row (SSM parameters and any live installs are NOT touched)'}
                  className='inline-flex items-center gap-1.5 rounded-md border border-red-500/30 bg-red-500/10 px-2 py-1 text-[11px] font-medium text-red-300 hover:border-red-500/50 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-40'
                >
                  {deletingAppKey === app.appKey
                    ? <LoadingSpinner />
                    : <TrashIcon className='size-3.5' aria-hidden='true' />}
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const ShopifyAppsCard = ({ auth0Token }: { auth0Token: string }): JSX.Element => {
  const [apps, setApps] = useState<MerchApp[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [listError, setListError] = useState<string | null>(null)

  const [form, setForm] = useState<AppFormState>(emptyAppForm())
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState<{ email: string, appKey: string } | null>(null)

  const [deletingAppKey, setDeletingAppKey] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [nowMs, setNowMs] = useState(() => Date.now())
  useEffect(() => {
    const id = globalThis.setInterval(() => setNowMs(Date.now()), 60_000)
    return () => globalThis.clearInterval(id)
  }, [])

  const load = useCallback(async (): Promise<void> => {
    setLoading(true)
    setListError(null)
    try {
      const { apps: fetched } = await graphqlRequest<{ apps: MerchApp[] }>(
        listMerchAppsQuery, {}, auth0Token
      )
      setApps([...fetched].sort((a, b) => a.appKey.localeCompare(b.appKey)))
    } catch (err) {
      setListError(err instanceof Error ? err.message : 'Failed to load the Shopify app registry')
    } finally {
      setLoading(false)
    }
  }, [auth0Token])

  useEffect(() => {
    void load()
  }, [load])

  const trimmedLink = form.installLinkUrl.trim()
  const linkValid = trimmedLink === '' || trimmedLink.startsWith('https://')
  const formValid =
    APP_KEY_RE.test(form.appKey.trim()) &&
    DISTRIBUTIONS.some((d) => d.value === form.distribution) &&
    form.ownerEmail.trim() !== '' &&
    linkValid

  const handleSave = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (!formValid || saving) return
    setSaving(true)
    setSaveError(null)
    setSaved(null)
    try {
      const label = form.label.trim()
      const ownerEmail = form.ownerEmail.trim()
      const { app } = await graphqlRequest<{ app: MerchApp }>(
        putMerchAppMutation,
        {
          appKey: form.appKey.trim(),
          distribution: form.distribution,
          ownerEmail,
          ...(label === '' ? {} : { label }),
          ...(trimmedLink === '' ? {} : { installLinkUrl: trimmedLink })
        },
        auth0Token
      )
      setSaved({ email: ownerEmail, appKey: app.appKey })
      setForm(emptyAppForm())
      void load()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save the app')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (app: MerchApp): Promise<void> => {
    if (deletingAppKey !== null) return
    const ok = globalThis.confirm(
      `Delete the registry row for ${app.appKey}? This removes StagePilot's operator metadata only the SSM credential parameters stay, and any store that already installed the app stays installed. Webhooks keep verifying.`
    )
    if (!ok) return
    setDeletingAppKey(app.appKey)
    setDeleteError(null)
    try {
      await graphqlRequest<{ result: DeletionResult }>(
        deleteMerchAppMutation, { appKey: app.appKey }, auth0Token
      )
      setApps((prev) => prev === null ? prev : prev.filter((a) => a.appKey !== app.appKey))
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete the app')
    } finally {
      setDeletingAppKey(null)
    }
  }

  const inputClass = 'w-full rounded-md border border-white/10 bg-dm-bg-accent/40 px-3 py-2 text-sm text-dm-text-primary placeholder:text-dm-text-accent focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-60'

  return (
    <div
      data-testid='shopify-apps-card'
      className='mt-6 rounded-xl border border-dashed border-white/15 bg-dm-bg-secondary p-6'
    >
      <div className='flex items-center justify-between gap-3'>
        <div className='flex items-center gap-2'>
          <h3 className='text-base font-semibold text-dm-text-primary'>Shopify apps</h3>
          {apps !== null && (
            <span className='inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-brand/15 px-2 text-xs font-semibold text-brand'>
              {apps.length}
            </span>
          )}
        </div>
        <button
          onClick={() => { void load() }}
          disabled={loading}
          title='Refresh the Shopify app registry (this card only)'
          className='inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
        >
          <ArrowPathIcon className={`size-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden='true' />
          Refresh
        </button>
      </div>
      <p className='mt-1.5 text-xs text-dm-text-secondary'>
        One app key per Shopify app. Custom distribution means one app per
        client, created in <span className='font-medium text-dm-text-primary'>our</span> Partner
        Dashboard never in the merchant&apos;s admin and installed by
        the merchant clicking the link below. Credentials live in SSM
        only: nothing on this card can show or store a client secret.
      </p>
      {deleteError !== null && (
        <div className='mt-3'>
          <ErrorInline onDismiss={() => setDeleteError(null)}>{deleteError}</ErrorInline>
        </div>
      )}
      <AppsBody
        apps={apps}
        loading={loading}
        error={listError}
        nowMs={nowMs}
        deletingAppKey={deletingAppKey}
        onDelete={(app) => { void handleDelete(app) }}
        onRetry={() => { void load() }}
      />

      <div className='mt-6 border-t border-white/10 pt-5'>
        <h4 className='text-sm font-semibold text-dm-text-primary'>Add or update an app</h4>
        <p className='mt-1.5 text-xs text-dm-text-secondary'>
          Do the SSM step first: <span className='font-mono text-[11px] text-dm-text-primary'>/shopify/&lt;stage&gt;/apps/&lt;appKey&gt;/</span>
          {' '}must already hold <span className='font-mono text-[11px]'>client_id</span>,
          {' '}<span className='font-mono text-[11px]'>client_secret</span> and
          {' '}<span className='font-mono text-[11px]'>distribution</span>. The distribution you
          pick here is checked against SSM and refused on mismatch. Name
          the owner by <span className='font-medium text-dm-text-primary'>email</span>  the
          StagePilot account every shop under this app key binds to; we
          resolve the account for you, so the account must already exist.
          Paste the install link generated in the Dev/Partner Dashboard
          it expires 7 days after Shopify issues it, so send it to the
          client the same day.
        </p>
        <form
          onSubmit={(e) => { void handleSave(e) }}
          className='mt-4 grid grid-cols-1 gap-2 md:grid-cols-2'
        >
          <input
            type='text'
            value={form.appKey}
            onChange={(e) => setForm((f) => ({ ...f, appKey: e.target.value }))}
            placeholder='app key (lowercase, digits, dashes)'
            disabled={saving}
            autoComplete='off'
            data-testid='merch-app-key-input'
            className={inputClass}
          />
          <input
            type='text'
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            placeholder='label (optional e.g. the client&apos;s name)'
            disabled={saving}
            autoComplete='off'
            data-testid='merch-app-label-input'
            className={inputClass}
          />
          <select
            value={form.distribution}
            onChange={(e) => setForm((f) => ({ ...f, distribution: e.target.value }))}
            disabled={saving}
            data-testid='merch-app-distribution-select'
            className={inputClass}
          >
            {DISTRIBUTIONS.map((d) => (
              <option key={d.value} value={d.value}>{d.label}</option>
            ))}
          </select>
          <input
            type='email'
            value={form.ownerEmail}
            onChange={(e) => setForm((f) => ({ ...f, ownerEmail: e.target.value }))}
            placeholder='owner@example.com'
            disabled={saving}
            autoComplete='off'
            data-testid='merch-app-owner-email-input'
            className={inputClass}
          />
          <input
            type='text'
            value={form.installLinkUrl}
            onChange={(e) => setForm((f) => ({ ...f, installLinkUrl: e.target.value }))}
            placeholder='install link from the Dev/Partner Dashboard (https://…)'
            disabled={saving}
            autoComplete='off'
            data-testid='merch-app-link-input'
            className={inputClass}
          />
          <div className='flex items-center'>
            <button
              type='submit'
              disabled={saving || !formValid}
              data-testid='merch-app-submit'
              className='inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {saving && <LoadingSpinner />}
              Save app
            </button>
          </div>
        </form>
        {!linkValid && (
          <p className='mt-2 text-xs text-amber-300'>
            The install link must start with https://
          </p>
        )}
        {saved !== null && (
          <p data-testid='merch-app-save-result' className='mt-3 text-xs text-emerald-300'>
            {saveResultCopy(saved)} Send the install link to the client
            they click it, approve on Shopify, and land on the connected
            page. Nothing else is needed from them.
          </p>
        )}
        {saveError !== null && (
          <div className='mt-3'>
            <ErrorInline onDismiss={() => setSaveError(null)}>{saveError}</ErrorInline>
          </div>
        )}
      </div>
    </div>
  )
}
